// src/components/EmailLandingPages.jsx
// =============================================================================
// Landing pages for links inside Monthly Water Report emails. The SPA fallback
// in vercel.json routes every path to index.html, so these paths always loaded
// the app; until now nothing consumed them, which is why every subscriber
// since April sat at opt_in_status = 'pending' (root-caused Jul 17 2026):
//
//   /confirm-water-report?token=...  -> ConfirmWaterReport
//        POSTs the token to /api/water-report/confirm on mount and shows the
//        outcome. Server statuses: 200 confirmed (or already confirmed),
//        404 invalid, 410 expired.
//
//   /unsubscribe?email=...           -> UnsubscribeWaterReport
//        Requires a button tap before POSTing to /api/water-report/unsubscribe.
//        DELIBERATE: mail clients and security scanners prefetch links, so a
//        confirm-on-load unsubscribe would silently remove subscribers who
//        never tapped anything. Confirmation stays one explicit tap.
//
// Both endpoints are public server-side; no auth headers are sent.
// =============================================================================

import React, { useEffect, useState } from 'react';

const API_BASE = 'https://generosity-sales-engine-mvp-api.onrender.com';

const S = {
  page: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', padding: 32,
    fontFamily: "Montserrat, 'Helvetica Neue', sans-serif",
    textAlign: 'center', background: '#FFFFFF',
  },
  wordmark: { fontSize: 22, fontWeight: 800, letterSpacing: 5, color: '#1F6FA0', marginBottom: 4 },
  wordmarkSub: { fontSize: 8, fontWeight: 600, letterSpacing: 3, color: '#A6A8AB', marginBottom: 28, textTransform: 'uppercase' },
  title: { fontSize: 21, fontWeight: 700, color: '#0F1419', marginBottom: 10, maxWidth: 340, lineHeight: 1.3 },
  body: { fontSize: 13.5, color: '#3D4043', marginBottom: 22, maxWidth: 320, lineHeight: 1.6 },
  btn: {
    background: '#51B0E6', color: '#FFFFFF', border: 'none', padding: '13px 26px',
    borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: 'pointer',
    letterSpacing: 0.4, fontFamily: 'inherit',
  },
  quietBtn: {
    background: 'transparent', color: '#6E7174', border: '1px solid rgba(15,20,25,0.08)',
    padding: '11px 20px', borderRadius: 12, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit', marginTop: 10,
  },
  spinner: {
    width: 26, height: 26, borderRadius: '50%', border: '3px solid #51B0E6',
    borderTopColor: 'transparent', animation: 'spin 1s linear infinite',
    marginBottom: 18,
  },
  badge: (bg, border) => ({
    width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: bg, border: `2px solid ${border}`,
    marginBottom: 16, fontSize: 24,
  }),
};

function Shell({ children }) {
  return (
    <div style={S.page}>
      <style>{'@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}'}</style>
      <div style={S.wordmark}>GENEROSITY{'\u2122'}</div>
      <div style={S.wordmarkSub}>Water Intelligence</div>
      {children}
    </div>
  );
}

function OpenAppButton() {
  return (
    <button type="button" style={S.btn} onClick={() => { window.location.href = '/'; }}>
      OPEN THE WTR APP {'\u2192'}
    </button>
  );
}

// ── CONFIRM ─────────────────────────────────────────────────────────────────
export function ConfirmWaterReport() {
  const [state, setState] = useState('working'); // working | confirmed | expired | invalid | error

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) { setState('invalid'); return; }
    let cancelled = false;
    fetch(`${API_BASE}/api/water-report/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((r) => {
        if (cancelled) return;
        if (r.status === 200) setState('confirmed');
        else if (r.status === 410) setState('expired');
        else if (r.status === 404) setState('invalid');
        else setState('error');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, []);

  if (state === 'working') {
    return (
      <Shell>
        <div style={S.spinner} />
        <div style={S.title}>Confirming your enrollment{'\u2026'}</div>
      </Shell>
    );
  }
  if (state === 'confirmed') {
    return (
      <Shell>
        <div style={S.badge('rgba(74,138,111,0.10)', 'rgba(74,138,111,0.30)')}>
          <span style={{ color: '#4A8A6F' }}>{'\u2713'}</span>
        </div>
        <div style={S.title}>You're enrolled.</div>
        <div style={S.body}>
          Your Monthly Water Intelligence Report arrives the 1st of every month. A welcome email is on its way.
        </div>
        <OpenAppButton />
      </Shell>
    );
  }
  if (state === 'expired') {
    return (
      <Shell>
        <div style={S.badge('rgba(200,155,60,0.10)', 'rgba(200,155,60,0.30)')}>
          <span style={{ color: '#C89B3C' }}>{'\u23F1'}</span>
        </div>
        <div style={S.title}>This link has expired.</div>
        <div style={S.body}>
          Confirmation links last 72 hours. Open the WTR App, run your water report, and tap Enable on the Monthly Water Report card to get a fresh link.
        </div>
        <OpenAppButton />
      </Shell>
    );
  }
  if (state === 'invalid') {
    return (
      <Shell>
        <div style={S.badge('rgba(184,74,74,0.08)', 'rgba(184,74,74,0.25)')}>
          <span style={{ color: '#B84A4A' }}>{'\u2715'}</span>
        </div>
        <div style={S.title}>This link isn't valid.</div>
        <div style={S.body}>
          It may have already been used. If you believe you're enrolled, you're all set. Otherwise, re-enable the Monthly Water Report from the WTR App.
        </div>
        <OpenAppButton />
      </Shell>
    );
  }
  return (
    <Shell>
      <div style={S.title}>Something went wrong.</div>
      <div style={S.body}>We couldn't reach the confirmation service. Check your connection and tap the email link again.</div>
      <OpenAppButton />
    </Shell>
  );
}

// ── UNSUBSCRIBE ─────────────────────────────────────────────────────────────
export function UnsubscribeWaterReport() {
  const email = (new URLSearchParams(window.location.search).get('email') || '').trim().toLowerCase();
  const [state, setState] = useState(email ? 'ask' : 'invalid'); // ask | working | done | invalid | error

  const doUnsubscribe = () => {
    setState('working');
    fetch(`${API_BASE}/api/water-report/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
      .then((r) => setState(r.ok ? 'done' : 'error'))
      .catch(() => setState('error'));
  };

  if (state === 'ask') {
    return (
      <Shell>
        <div style={S.title}>Unsubscribe from the Monthly Water Report?</div>
        <div style={S.body}>
          {email} will stop receiving the Monthly Water Intelligence Report. You can re-enable it any time from the WTR App.
        </div>
        <button type="button" style={S.btn} onClick={doUnsubscribe}>UNSUBSCRIBE</button>
        <button type="button" style={S.quietBtn} onClick={() => { window.location.href = '/'; }}>
          Keep my report
        </button>
      </Shell>
    );
  }
  if (state === 'working') {
    return (
      <Shell>
        <div style={S.spinner} />
        <div style={S.title}>Removing you{'\u2026'}</div>
      </Shell>
    );
  }
  if (state === 'done') {
    return (
      <Shell>
        <div style={S.badge('rgba(74,138,111,0.10)', 'rgba(74,138,111,0.30)')}>
          <span style={{ color: '#4A8A6F' }}>{'\u2713'}</span>
        </div>
        <div style={S.title}>You're unsubscribed.</div>
        <div style={S.body}>No more monthly reports will be sent to {email}. Changed your mind? Re-enable it from the WTR App any time.</div>
        <OpenAppButton />
      </Shell>
    );
  }
  if (state === 'invalid') {
    return (
      <Shell>
        <div style={S.title}>This link is missing information.</div>
        <div style={S.body}>Use the unsubscribe link from the bottom of a report email.</div>
        <OpenAppButton />
      </Shell>
    );
  }
  return (
    <Shell>
      <div style={S.title}>Something went wrong.</div>
      <div style={S.body}>We couldn't reach the service. Tap the email link again in a moment.</div>
      <OpenAppButton />
    </Shell>
  );
}
